import { api } from './client';
import type {
  AdminPayoutPage,
  AdminPayoutRow,
  AdminRefundPage,
  AdminRefundRow,
  AdminRole,
  AdminServiceRow,
  AdminStateHistoryEntry,
  AdminTransactionPage,
  AdminTransactionRow,
  AdminUserPage,
  AdminUserRow,
  AssociateFeedback,
  CreateServicePayload,
  InvoiceDetail,
  InvoiceListPage,
  InvoicePaymentMode,
  InvoiceStatus,
  IsoDate,
  KycStatus,
  PayoutStatus,
  PlatformConfig,
  Profession,
  RefundStatus,
  TransactionStatus,
  UpdatePlatformConfigPayload,
  UpdateServicePayload,
  AuthorityRow,
  AuthorityServiceRow,
  BulkImportAuthoritiesPayload,
  BulkImportAuthoritiesResponse,
  CreateAuthorityPayload,
  CreateAuthorityServicePayload,
  UpdateAuthorityPayload,
  UpdateAuthorityServicePayload,
  UserRole,
  UserStatus,
  Uuid,
  AuditLogMeta,
  AuditLogPage,
  AuditLogRow,
  AuditLogStats,
} from '../types/api';

export type {
  AuthorityRow,
  AuthorityServiceRow,
  BulkImportAuthoritiesPayload,
  BulkImportAuthoritiesResponse,
  CreateAuthorityPayload,
  CreateAuthorityServicePayload,
  UpdateAuthorityPayload,
  UpdateAuthorityServicePayload,
};

// ============================================================
// Admin — KYC
// ============================================================

export interface AdminKycListItem {
  id: Uuid;
  userId: Uuid;
  aadhaarLast4: string | null;
  panNumber: string | null;
  bankAccountLast4: string | null;
  bankName: string | null;
  bankBranch: string | null;
  ifscCode: string | null;
  gstin: string | null;
  address: string | null;
  submittedAt: IsoDate;
  verifiedAt: IsoDate | null;
  verifiedByAdminId: Uuid | null;
  rejectionReason: string | null;
  aadhaarDocS3Key: string | null;
  panDocS3Key: string | null;
  user: {
    id: Uuid;
    email: string;
    firstName: string | null;
    lastName: string | null;
    mobile: string | null;
    associateProfile: {
      profession: Profession;
      isFranchise: boolean;
      outletName: string | null;
      kycStatus: KycStatus;
      kycSubmittedAt: IsoDate | null;
      kycVerifiedAt: IsoDate | null;
    } | null;
  };
}

export interface AdminKycListPage {
  items: AdminKycListItem[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface ListAdminKycParams {
  status?: KycStatus;
  page?: number;
  pageSize?: number;
}

export async function listAdminKyc(params: ListAdminKycParams = {}): Promise<AdminKycListPage> {
  const { data } = await api.get<AdminKycListPage>('/admin/kyc', { params });
  return data;
}

export async function getAdminKyc(id: Uuid): Promise<AdminKycListItem> {
  const { data } = await api.get<AdminKycListItem>(`/admin/kyc/${id}`);
  return data;
}

export async function approveAdminKyc(id: Uuid): Promise<AdminKycListItem> {
  const { data } = await api.post<AdminKycListItem>(`/admin/kyc/${id}/approve`);
  return data;
}

export async function rejectAdminKyc(id: Uuid, reason: string): Promise<AdminKycListItem> {
  const { data } = await api.post<AdminKycListItem>(`/admin/kyc/${id}/reject`, { reason });
  return data;
}

export type KycDocType = 'AADHAAR' | 'PAN';

export interface KycDocumentResponse {
  url: string;
  expiresInSeconds: number;
  expiresAt: string;
}

export async function getAdminKycDocumentUrl(
  id: Uuid,
  type: KycDocType,
): Promise<KycDocumentResponse> {
  const { data } = await api.get<KycDocumentResponse>(`/admin/kyc/${id}/document`, {
    params: { type },
  });
  return data;
}

// ============================================================
// Admin — Users
// ============================================================

export interface ListAdminUsersParams {
  role?: UserRole;
  adminRole?: AdminRole;
  status?: UserStatus;
  serviceId?: Uuid;
  q?: string;
  page?: number;
  pageSize?: number;
}

export async function listAdminUsers(params: ListAdminUsersParams = {}): Promise<AdminUserPage> {
  const { data } = await api.get<AdminUserPage>('/admin/users', { params });
  return data;
}

export async function getAdminUser(id: Uuid): Promise<AdminUserRow> {
  const { data } = await api.get<AdminUserRow>(`/admin/users/${id}`);
  return data;
}

export async function suspendAdminUser(id: Uuid, reason?: string): Promise<AdminUserRow> {
  const { data } = await api.post<AdminUserRow>(`/admin/users/${id}/suspend`, { reason });
  return data;
}

export async function reinstateAdminUser(id: Uuid): Promise<AdminUserRow> {
  const { data } = await api.post<AdminUserRow>(`/admin/users/${id}/reinstate`);
  return data;
}

export async function deleteAdminUser(id: Uuid): Promise<{ success: boolean }> {
  const { data } = await api.post<{ success: boolean }>(`/admin/users/${id}/delete`);
  return data;
}

export async function setAdminRole(id: Uuid, adminRole: AdminRole | null): Promise<AdminUserRow> {
  const { data } = await api.post<AdminUserRow>(`/admin/users/${id}/admin-role`, { adminRole });
  return data;
}

export async function getAdminUserFeedbacks(id: Uuid): Promise<AssociateFeedback[]> {
  const { data } = await api.get<AssociateFeedback[]>(`/admin/users/${id}/feedbacks`);
  return data;
}

// ============================================================
// Admin — Transactions
// ============================================================

export interface ListAdminTxParams {
  status?: TransactionStatus;
  clientId?: Uuid;
  assignedAssociateId?: Uuid;
  q?: string;
  unassigned?: boolean;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

export async function listAdminTransactions(
  params: ListAdminTxParams = {},
): Promise<AdminTransactionPage> {
  const { data } = await api.get<AdminTransactionPage>('/admin/transactions', { params });
  return data;
}

export async function getAdminTransaction(id: Uuid): Promise<AdminTransactionRow> {
  const { data } = await api.get<AdminTransactionRow>(`/admin/transactions/${id}`);
  return data;
}

export async function getAdminTransactionHistory(
  id: Uuid,
  limit = 50,
): Promise<AdminStateHistoryEntry[]> {
  const { data } = await api.get<AdminStateHistoryEntry[]>(
    `/admin/transactions/${id}/state-history`,
    { params: { limit } },
  );
  return data;
}

export async function assignAssociate(
  id: Uuid,
  payload: { associateUserId?: Uuid; associateUserIds?: Uuid[]; reason?: string },
): Promise<AdminTransactionRow> {
  const { data } = await api.post<AdminTransactionRow>(`/admin/transactions/${id}/assign`, payload);
  return data;
}

export async function revokeAssignment(id: Uuid, reason: string): Promise<AdminTransactionRow> {
  const { data } = await api.post<AdminTransactionRow>(
    `/admin/transactions/${id}/revoke-assignment`,
    { reason },
  );
  return data;
}

export async function forceCompleteTransaction(id: Uuid): Promise<AdminTransactionRow> {
  const { data } = await api.post<AdminTransactionRow>(`/admin/transactions/${id}/force-complete`);
  return data;
}

export async function forceCancelTransaction(
  id: Uuid,
  reason: string,
): Promise<AdminTransactionRow> {
  const { data } = await api.post<AdminTransactionRow>(`/admin/transactions/${id}/force-cancel`, {
    reason,
  });
  return data;
}

export async function reviewCancellationRequest(
  id: Uuid,
  payload: { action: 'ACCEPT' | 'REJECT'; reason?: string },
): Promise<AdminTransactionRow> {
  const { data } = await api.post<AdminTransactionRow>(
    `/transactions/${id}/review-cancellation`,
    payload,
  );
  return data;
}

// ============================================================
// Admin — Invoices
// ============================================================

export interface CreateInvoicePayload {
  transactionId: Uuid;
  paymentMode?: InvoicePaymentMode;
  lineItems?: Array<{
    description: string;
    amountPaise: string;
    quantity?: number;
    sortOrder?: number;
  }>;
  applicationFeePaise?: string;
  serviceChargePaise?: string;
  governmentFeePaise?: string;
  additionalCharges?: Array<{
    description: string;
    amountPaise: string;
    gstPercentage?: string;
    hsnSacCode?: string;
  }>;
  gstPercentageOverride?: string;
  associateSplitPercentageOverride?: string;
  clientGstin?: string;
  placeOfSupplyState: string;
  hsnSacCode?: string;
}

export async function createInvoice(payload: CreateInvoicePayload): Promise<InvoiceDetail> {
  const { data } = await api.post<InvoiceDetail>('/invoices', payload);
  return data;
}

export interface ListInvoicesParams {
  status?: InvoiceStatus;
  paymentMode?: InvoicePaymentMode;
  transactionId?: Uuid;
  fiscalYear?: string;
  page?: number;
  pageSize?: number;
}

export async function listInvoices(params: ListInvoicesParams = {}): Promise<InvoiceListPage> {
  const { data } = await api.get<InvoiceListPage>('/invoices', { params });
  return data;
}

export async function getInvoice(id: Uuid): Promise<InvoiceDetail> {
  const { data } = await api.get<InvoiceDetail>(`/invoices/${id}`);
  return data;
}

export async function markInvoiceSent(id: Uuid): Promise<InvoiceDetail> {
  const { data } = await api.post<InvoiceDetail>(`/invoices/${id}/mark-sent`);
  return data;
}

export async function cancelInvoice(id: Uuid, reason: string): Promise<InvoiceDetail> {
  const { data } = await api.post<InvoiceDetail>(`/invoices/${id}/cancel`, { reason });
  return data;
}

export async function markInvoicePaidOffline(
  id: Uuid,
  payload: { reference: string; notes?: string },
): Promise<InvoiceDetail> {
  const { data } = await api.post<InvoiceDetail>(`/invoices/${id}/mark-paid-offline`, payload);
  return data;
}

// ============================================================
// Admin — Refunds
// ============================================================

export interface ListRefundsParams {
  status?: RefundStatus;
  page?: number;
  pageSize?: number;
}

export async function listAdminRefunds(params: ListRefundsParams = {}): Promise<AdminRefundPage> {
  const { data } = await api.get<AdminRefundPage>('/admin/refunds', { params });
  return data;
}

export async function approveRefund(
  id: Uuid,
  payload: { finalRefundPaise?: string; notes?: string } = {},
): Promise<AdminRefundRow> {
  const { data } = await api.post<AdminRefundRow>(`/refunds/${id}/approve`, payload);
  return data;
}

export async function rejectRefund(id: Uuid, reason: string): Promise<AdminRefundRow> {
  const { data } = await api.post<AdminRefundRow>(`/refunds/${id}/reject`, { reason });
  return data;
}

export async function markRefundedOffline(
  id: Uuid,
  payload: { bankReference: string; finalRefundPaise?: string; notes?: string },
): Promise<AdminRefundRow> {
  const { data } = await api.post<AdminRefundRow>(`/refunds/${id}/mark-refunded-offline`, payload);
  return data;
}

// ============================================================
// Admin — Payouts
// ============================================================

export interface ListPayoutsParams {
  status?: PayoutStatus;
  associateUserId?: Uuid;
  transactionId?: Uuid;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

export async function listAdminPayouts(params: ListPayoutsParams = {}): Promise<AdminPayoutPage> {
  const { data } = await api.get<AdminPayoutPage>('/admin/payouts', { params });
  return data;
}

export async function getAdminPayout(id: Uuid): Promise<AdminPayoutRow> {
  const { data } = await api.get<AdminPayoutRow>(`/admin/payouts/${id}`);
  return data;
}

export interface AdminEligiblePayoutTransaction {
  id: Uuid;
  code: string;
  status: TransactionStatus;
  serviceName: string;
  associateName: string;
  grossPaise: string;
}

export async function getEligiblePayoutTransactions(): Promise<AdminEligiblePayoutTransaction[]> {
  const { data } = await api.get<AdminEligiblePayoutTransaction[]>('/admin/payouts/eligible-transactions');
  return data;
}

export async function createAdminPayout(transactionId: Uuid): Promise<AdminPayoutRow> {
  const { data } = await api.post<AdminPayoutRow>('/admin/payouts', { transactionId });
  return data;
}

export async function markPayoutPaid(
  id: Uuid,
  payload: { transactionReference: string; proofS3Key?: string; notes?: string },
): Promise<AdminPayoutRow> {
  const { data } = await api.post<AdminPayoutRow>(`/admin/payouts/${id}/mark-paid`, payload);
  return data;
}

export async function holdPayout(id: Uuid, notes: string): Promise<AdminPayoutRow> {
  const { data } = await api.post<AdminPayoutRow>(`/admin/payouts/${id}/hold`, { notes });
  return data;
}

export async function cancelPayout(id: Uuid, notes: string): Promise<AdminPayoutRow> {
  const { data } = await api.post<AdminPayoutRow>(`/admin/payouts/${id}/cancel`, { notes });
  return data;
}

// ============================================================
// Admin — Services (catalog)
// ============================================================

export async function adminCreateService(payload: CreateServicePayload): Promise<AdminServiceRow> {
  const { data } = await api.post<AdminServiceRow>('/admin/services', payload);
  return data;
}

export async function adminUpdateService(
  id: Uuid,
  payload: UpdateServicePayload,
): Promise<AdminServiceRow> {
  const { data } = await api.put<AdminServiceRow>(`/admin/services/${id}`, payload);
  return data;
}

export async function adminActivateService(id: Uuid): Promise<AdminServiceRow> {
  const { data } = await api.post<AdminServiceRow>(`/admin/services/${id}/activate`);
  return data;
}

export async function adminDeactivateService(id: Uuid): Promise<AdminServiceRow> {
  const { data } = await api.post<AdminServiceRow>(`/admin/services/${id}/deactivate`);
  return data;
}

// ============================================================
// Admin — Platform config
// ============================================================

export async function getPlatformConfig(): Promise<PlatformConfig> {
  const { data } = await api.get<PlatformConfig>('/admin/config');
  return data;
}

export async function updatePlatformConfig(
  payload: UpdatePlatformConfigPayload,
): Promise<PlatformConfig> {
  const { data } = await api.patch<PlatformConfig>('/admin/config', payload);
  return data;
}

// ============================================================
// Admin — Reports
// ============================================================

export interface ReportWindow {
  fromDate: string;
  toDate: string;
}

export async function gstReport(window: ReportWindow & { stateCode?: string }): Promise<unknown> {
  const { data } = await api.get<unknown>('/admin/reports/gst', { params: window });
  return data;
}

export async function tdsReport(window: ReportWindow & { sectionCode?: string }): Promise<unknown> {
  const { data } = await api.get<unknown>('/admin/reports/tds', { params: window });
  return data;
}

export async function revenueReport(
  window: ReportWindow & { bucket?: 'day' | 'month' },
): Promise<unknown> {
  const { data } = await api.get<unknown>('/admin/reports/revenue', { params: window });
  return data;
}

export interface ServiceImageUploadUrlPayload {
  contentType: string;
  contentLength: number;
}

export interface ServiceImageUploadUrlResponse {
  uploadUrl: string;
  key: string;
  bucket: string;
  expiresInSeconds: number;
  expiresAt: string;
  headers: Record<string, string>;
}

export async function requestServiceImageUploadUrl(
  payload: ServiceImageUploadUrlPayload,
): Promise<ServiceImageUploadUrlResponse> {
  const { data } = await api.post<ServiceImageUploadUrlResponse>(
    '/admin/services/image-upload-url',
    payload,
  );
  return data;
}

export async function uploadServiceImage(file: File): Promise<string> {
  const contentType = file.type || 'image/png';
  const presigned = await requestServiceImageUploadUrl({
    contentType,
    contentLength: file.size,
  });
  const headers: Record<string, string> = {};
  if (presigned.headers) {
    for (const [k, v] of Object.entries(presigned.headers)) {
      if (k.toLowerCase() !== 'content-length') {
        headers[k] = v;
      }
    }
  }
  if (!headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = contentType;
  }
  const putRes = await fetch(presigned.uploadUrl, {
    method: 'PUT',
    headers,
    body: file,
  });
  if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
  return presigned.key;
}

export async function adminReorderServices(payload: {
  ids: Uuid[];
  startIndex?: number;
}): Promise<{ success: boolean }> {
  const { data } = await api.post<{ success: boolean }>('/admin/services/reorder', payload);
  return data;
}

// ============================================================
// Admin — Authorities
// ============================================================

export async function listAuthorities(): Promise<AuthorityRow[]> {
  const { data } = await api.get<AuthorityRow[]>('/admin/authorities');
  return data;
}

export async function getAuthority(id: Uuid): Promise<AuthorityRow> {
  const { data } = await api.get<AuthorityRow>(`/admin/authorities/${id}`);
  return data;
}

export async function createAuthority(payload: CreateAuthorityPayload): Promise<AuthorityRow> {
  const { data } = await api.post<AuthorityRow>('/admin/authorities', payload);
  return data;
}

export async function updateAuthority(
  id: Uuid,
  payload: UpdateAuthorityPayload,
): Promise<AuthorityRow> {
  const { data } = await api.put<AuthorityRow>(`/admin/authorities/${id}`, payload);
  return data;
}

export async function archiveAuthority(id: Uuid): Promise<AuthorityRow> {
  const { data } = await api.put<AuthorityRow>(`/admin/authorities/${id}/archive`);
  return data;
}

export async function unarchiveAuthority(id: Uuid): Promise<AuthorityRow> {
  const { data } = await api.put<AuthorityRow>(`/admin/authorities/${id}/unarchive`);
  return data;
}

export async function deleteAuthority(id: Uuid): Promise<{ success: boolean; id: Uuid }> {
  const { data } = await api.delete<{ success: boolean; id: Uuid }>(`/admin/authorities/${id}`);
  return data;
}

export async function createAuthorityService(
  payload: CreateAuthorityServicePayload,
): Promise<AuthorityServiceRow> {
  const { data } = await api.post<AuthorityServiceRow>('/admin/authorities/services', payload);
  return data;
}

export async function updateAuthorityService(
  serviceId: Uuid,
  payload: UpdateAuthorityServicePayload,
): Promise<AuthorityServiceRow> {
  const { data } = await api.put<AuthorityServiceRow>(
    `/admin/authorities/services/${serviceId}`,
    payload,
  );
  return data;
}

export async function archiveAuthorityService(serviceId: Uuid): Promise<AuthorityServiceRow> {
  const { data } = await api.put<AuthorityServiceRow>(
    `/admin/authorities/services/${serviceId}/archive`,
  );
  return data;
}

export async function unarchiveAuthorityService(serviceId: Uuid): Promise<AuthorityServiceRow> {
  const { data } = await api.put<AuthorityServiceRow>(
    `/admin/authorities/services/${serviceId}/unarchive`,
  );
  return data;
}

export async function deleteAuthorityService(
  serviceId: Uuid,
): Promise<{ success: boolean; id: Uuid }> {
  const { data } = await api.delete<{ success: boolean; id: Uuid }>(
    `/admin/authorities/services/${serviceId}`,
  );
  return data;
}

export async function bulkImportAuthorities(
  payload: BulkImportAuthoritiesPayload,
): Promise<BulkImportAuthoritiesResponse> {
  const { data } = await api.post<BulkImportAuthoritiesResponse>(
    '/admin/authorities/import',
    payload,
  );
  return data;
}

// ============================================================
// Admin — Audit Logs
// ============================================================

export interface ListAdminAuditLogsParams {
  q?: string;
  action?: string;
  entityType?: string;
  actorUserId?: Uuid;
  fromDate?: string;
  toDate?: string;
  page?: number;
  pageSize?: number;
}

export async function listAdminAuditLogs(
  params: ListAdminAuditLogsParams = {},
): Promise<AuditLogPage> {
  const { data } = await api.get<AuditLogPage>('/admin/audit-logs', { params });
  return data;
}

export async function getAdminAuditLog(id: Uuid): Promise<AuditLogRow> {
  const { data } = await api.get<AuditLogRow>(`/admin/audit-logs/${id}`);
  return data;
}

export async function getAdminAuditLogMeta(): Promise<AuditLogMeta> {
  const { data } = await api.get<AuditLogMeta>('/admin/audit-logs/meta');
  return data;
}

export async function getAdminAuditLogStats(): Promise<AuditLogStats> {
  const { data } = await api.get<AuditLogStats>('/admin/audit-logs/stats');
  return data;
}
